/**
 * Reachability tab — Status donut + Attempts curve + Top slots + Heatmap
 *                    + Unreachable list. Extracted from AnalyticsCalls.jsx
 *                    (iter29 refactor). Iso-behaviour.
 */
import { Loader2, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { AiCommentaryBlock } from "./AiCommentaryBlock";
import { DAYS_FR, STATUS_TONE } from "./shared";

export function ReachabilityTab({ filters }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.get("/analytics/calls/reachability", {
            params: {
                from: filters.from,
                to: filters.to,
                offer_id: filters.offer_id || undefined,
                recruiter_id: filters.recruiter_id || undefined,
                source: filters.source || undefined,
            },
        }).then(({ data }) => {
            if (!cancelled) setData(data);
        }).catch(() => {
            if (!cancelled) toast.error("Impossible de charger la joignabilité");
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [filters]);

    if (loading || !data) {
        return <div className="text-slate-400 text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
    }

    return (
        <div className="space-y-5">
            <AiCommentaryBlock scope="reachability" filters={filters} testid="ai-commentary-reachability" />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <StatusDonut items={data.status_distribution} />
                <AttemptsCurve items={data.attempts_curve} summary={data.attempts_summary} />
            </div>

            <TopSlots slots={data.top_slots} confidence={data.top_slots_confidence} />

            <Heatmap grid={data.heatmap} />

            <UnreachableList items={data.unreachable} />
        </div>
    );
}

function StatusDonut({ items }) {
    return (
        <div className="bg-white rounded-2xl border-2 border-slate-100 p-5">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3">
                Répartition des statuts
            </div>
            <div className="space-y-2" data-testid="status-donut">
                {items.length === 0 && <div className="text-slate-400 text-sm italic">Aucun appel.</div>}
                {items.map((it) => {
                    const tone = STATUS_TONE[it.status] || STATUS_TONE.technical_failure;
                    return (
                        <div key={it.status} className="flex items-center gap-3">
                            <div className="text-[11px] font-semibold text-slate-600 w-48 shrink-0">{tone.label}</div>
                            <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                                <div className={`h-full ${tone.bg} rounded-full transition-all`} style={{ width: `${it.share_pct}%` }} />
                            </div>
                            <div className="text-xs font-black text-shariti-black w-20 text-right shrink-0">
                                {it.count} · {it.share_pct}%
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function AttemptsCurve({ items, summary }) {
    return (
        <div className="bg-white rounded-2xl border-2 border-slate-100 p-5">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3">
                Rendement par n° de tentative
            </div>
            <div className="text-[11px] text-slate-500 mb-2">
                Moyenne avant 1<sup>er</sup> contact : <strong className="text-shariti-black">
                    {summary.avg_attempts_before_reach ?? "—"}
                </strong> tentatives · Cand. avec ≥3 tentatives : <strong className="text-shariti-black">{summary.candidates_with_3_plus}</strong>
            </div>
            <div className="flex items-end gap-2 h-32" data-testid="attempts-curve">
                {items.length === 0 && <div className="text-slate-400 italic self-center w-full text-center">Pas de données.</div>}
                {items.map((b) => {
                    const h = b.reach_rate;
                    return (
                        <div key={b.attempt} className="flex-1 flex flex-col items-center gap-1" title={`Tentative ${b.attempt} : ${b.reached}/${b.total} joints`}>
                            <div className="w-full flex flex-col items-center justify-end h-24">
                                <div className="text-[9px] font-black text-shariti-black mb-0.5">{b.reach_rate}%</div>
                                <div className="w-full rounded-t bg-shariti-orange" style={{ height: `${Math.max(h, 3)}%` }} />
                            </div>
                            <div className="text-[9px] font-bold text-slate-400">n°{b.attempt}</div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function TopSlots({ slots, confidence }) {
    // P0 bug fix (2026-07-22): the backend now guarantees every returned
    // slot has ≥3 answered candidates AND reach_rate > 0. When the dataset
    // doesn't have enough answered calls, `confidence === "insufficient_data"`
    // and we render an explicit message instead of a misleading "0%"
    // recommendation.
    if (confidence === "insufficient_data" || !slots?.length) {
        return (
            <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 p-5 text-center" data-testid="top-slots-insufficient">
                <div className="flex items-center justify-center gap-2 mb-1">
                    <Sparkles className="h-4 w-4 text-slate-300" />
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                        Créneaux recommandés
                    </div>
                </div>
                <div className="text-xs text-slate-400 mt-1 max-w-md mx-auto">
                    Données insuffisantes pour recommander des créneaux — il faut au moins 3 candidats joints sur un même créneau.
                </div>
            </div>
        );
    }
    return (
        <div className="rounded-2xl border-2 border-shariti-orange/30 bg-shariti-orange-soft p-5" data-testid="top-slots">
            <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-4 w-4 text-shariti-orange" />
                <div className="text-[10px] font-black uppercase tracking-widest text-shariti-orange">
                    Créneaux recommandés · meilleurs taux de décroché
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {slots.map((s, i) => (
                    <div key={i} className="rounded-xl bg-white p-3 border border-shariti-orange/30" data-testid={`top-slot-${i}`}>
                        <div className="text-xs font-black text-shariti-black">
                            {s.day_label} · {String(s.hour).padStart(2, "0")}h–{String(s.hour + 1).padStart(2, "0")}h
                        </div>
                        <div className="mt-1 flex items-baseline gap-2">
                            <span className="text-2xl font-black text-shariti-orange">{s.reach_rate}%</span>
                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                décroché · {s.reached}/{s.total} joints
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Heatmap({ grid }) {
    return (
        <div className="bg-white rounded-2xl border-2 border-slate-100 p-5">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3">
                Heatmap · Taux de décroché par jour × créneau
            </div>
            <div className="overflow-x-auto" data-testid="heatmap">
                <table className="w-full text-[10px]">
                    <thead>
                        <tr>
                            <th className="text-slate-400 font-bold px-1 py-0.5"></th>
                            {Array.from({ length: 24 }, (_, h) => (
                                <th key={h} className="text-slate-400 font-bold w-6 text-center">
                                    {h % 3 === 0 ? h : ""}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {grid.map((row, d) => (
                            <tr key={d}>
                                <td className="text-slate-500 font-black uppercase text-[9px] pr-2">{DAYS_FR[d]}</td>
                                {row.map((cell, h) => {
                                    const intensity = cell.total === 0 ? 0 : Math.min(cell.reach_rate, 100);
                                    const bgOpacity = cell.total === 0 ? 0 : Math.max(0.12, intensity / 100);
                                    return (
                                        <td key={h} className="p-0.5 text-center">
                                            <div
                                                title={cell.total ? `${DAYS_FR[d]} ${h}h : ${cell.reached}/${cell.total} · ${cell.reach_rate}%` : "Aucun appel"}
                                                className="h-6 rounded transition"
                                                style={{
                                                    backgroundColor: cell.total === 0 ? "#f1f5f9" : `rgba(238,114,46,${bgOpacity})`,
                                                }}
                                            />
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function UnreachableList({ items }) {
    if (!items?.length) return null;
    return (
        <div className="bg-white rounded-2xl border-2 border-slate-100 p-5">
            <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                    Injoignables · 3+ tentatives, 0 contact
                </div>
                <div className="text-[10px] font-black text-shariti-red">{items.length} candidat{items.length > 1 ? "s" : ""}</div>
            </div>
            <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto" data-testid="unreachable-list">
                {items.map((u) => {
                    const tone = STATUS_TONE[u.last_status] || STATUS_TONE.technical_failure;
                    return (
                        <a
                            key={u.candidate_id}
                            href={`/candidats/${u.candidate_id}`}
                            className="block py-2.5 hover:bg-slate-50 transition group"
                        >
                            <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-bold text-shariti-black break-words group-hover:text-shariti-orange transition">
                                        {u.name}
                                    </div>
                                    <div className="text-xs text-slate-500 break-words">
                                        {u.offer_title || "—"} · {u.phone || "n° inconnu"}
                                    </div>
                                </div>
                                <div className="shrink-0 text-right">
                                    <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-black uppercase ${tone.soft} ${tone.text}`}>
                                        {tone.label}
                                    </span>
                                    <div className="text-[10px] font-bold text-slate-400 mt-0.5">
                                        {u.attempts} tentatives
                                    </div>
                                </div>
                            </div>
                        </a>
                    );
                })}
            </div>
        </div>
    );
}
